// StudentProfile — one page per student: identity + parent contact header,
// a Total / Paid / Remaining fee strip with Notify Dues, and tabs for
// Attendance, Fees, Transport, Results and Siblings. Opened from the
// Students list, the top-bar search, the dashboard and the fee reports.
import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, Phone, Mail, MapPin, Bell, Pencil, Bus, Smartphone, ChevronLeft, ChevronRight,
  Camera, IdCard as IdCardIcon, FileBadge,
} from 'lucide-react';
import {
  getStudentProfile, getStudentFees, sendFeeReminder, getStudentAttendanceMonth,
  uploadStudentPhoto, deleteStudentPhoto, getIdCards, getClassHistory, getCertificates,
} from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { inr, fmtDate, fmtTime, fmtMonth, initials, todayISO, resizeImage, sessionEndISO } from '../ui/format';
import { IdCardSheet } from './IdCard';
import FeesTab from './profile/FeesTab';
import ResultsTab from './profile/ResultsTab';
import { PaymentsTab, DocumentsTab, RemarksTab, LeaveTab } from './profile/RecordsTabs';

const TABS = [
  { key: 'attendance', label: 'Attendance' },
  { key: 'fees',       label: 'Fees' },
  { key: 'payments',   label: 'Payments' },
  { key: 'results',    label: 'Results' },
  { key: 'transport',  label: 'Transport' },
  { key: 'remarks',    label: 'Remarks' },
  { key: 'leave',      label: 'Leave' },
  { key: 'documents',  label: 'Documents' },
  { key: 'siblings',   label: 'Siblings' },
  { key: 'history',    label: 'History' },
];

export default function StudentProfile({ studentId, initialTab = 'attendance', onBack, onOpenStudent, onEdit, onCertificate, schoolName, board }) {
  const [profile, setProfile] = useState(null);
  const [fees,    setFees]    = useState(null);
  const [tab,     setTab]     = useState(initialTab);
  const [msg,     setMsg]     = useState('');
  const [notice,  setNotice]  = useState('');
  const [sending, setSending] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [idCard,  setIdCard]  = useState(null); // { school, students } while the print sheet is open
  const fileRef = useRef(null);

  useEffect(() => {
    setProfile(null); setFees(null); setMsg(''); setNotice(''); setTab(initialTab);
    Promise.all([getStudentProfile(studentId), getStudentFees(studentId)])
      .then(([p, f]) => { setProfile(p); setFees(f); })
      .catch(err => setMsg('Failed to load student: ' + (err.response?.data?.error || err.message)));
  }, [studentId, initialTab]);

  async function notifyDues() {
    setSending(true); setNotice('');
    try {
      const r = await sendFeeReminder(studentId);
      setNotice(r.notified
        ? 'Reminder sent to the guardian app ✅'
        : 'Reminder saved — the guardian will see it next time they open the app (no push token on file).');
    } catch (err) {
      setNotice('Error: ' + (err.response?.data?.error || err.message));
    } finally { setSending(false); }
  }

  async function onPhotoChosen(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoBusy(true); setNotice('');
    try {
      const dataUrl = await resizeImage(file);
      await uploadStudentPhoto(studentId, dataUrl);
      setProfile(p => ({ ...p, student: { ...p.student, photo: dataUrl } }));
      setNotice('Photo saved ✅');
    } catch (err) {
      setNotice('Error: ' + (err.response?.data?.error || err.message));
    } finally { setPhotoBusy(false); }
  }

  async function removePhoto() {
    if (!window.confirm('Remove this student\'s photo?')) return;
    setPhotoBusy(true); setNotice('');
    try {
      await deleteStudentPhoto(studentId);
      setProfile(p => ({ ...p, student: { ...p.student, photo: null } }));
    } catch (err) {
      setNotice('Error: ' + (err.response?.data?.error || err.message));
    } finally { setPhotoBusy(false); }
  }

  async function openIdCard() {
    try { setIdCard(await getIdCards({ ids: String(studentId) })); }
    catch (err) { setNotice('Error: ' + (err.response?.data?.error || err.message)); }
  }

  if (msg) return <div><BackLink onBack={onBack} /><p className="text-critical font-bold text-sm">{msg}</p></div>;
  if (!profile) return <div><BackLink onBack={onBack} /><p className="text-ink-soft text-sm">Loading…</p></div>;

  const s = profile.student;
  const configured = fees?.configured;
  const total = configured ? fees.amount_paid + fees.amount_due : 0;

  return (
    <div className="max-w-[1100px]">
      <BackLink onBack={onBack} />

      {/* ── Header ───────────────────────────────────────── */}
      <Card className="p-5 mb-4">
        <div className="flex flex-wrap items-start gap-5">
          <div className="flex flex-col items-center gap-1.5 shrink-0">
            {s.photo ? (
              <img src={s.photo} alt={s.name} className="w-[88px] h-[104px] rounded-m object-cover border border-line" />
            ) : (
              <div className="w-[88px] h-[104px] rounded-m bg-transit text-white grid place-items-center font-display font-extrabold text-3xl">
                {initials(s.name)}
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoChosen} />
            <button onClick={() => fileRef.current?.click()} disabled={photoBusy}
              className="flex items-center gap-1 text-xs font-bold text-transit-deep hover:underline disabled:opacity-50">
              <Camera size={12} /> {photoBusy ? 'Saving…' : s.photo ? 'Change photo' : 'Add photo'}
            </button>
            {s.photo && !photoBusy && (
              <button onClick={removePhoto} className="text-[11px] font-semibold text-ink-soft hover:text-critical">Remove</button>
            )}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display font-extrabold text-2xl text-ink">{s.name}</h2>
              {s.status !== 'active' && <Badge tone={s.status === 'alumni' ? 'info' : 'caution'}>{s.status === 'alumni' ? 'Alumni' : 'Left school'}{s.date_of_leaving ? ` · ${fmtDate(s.date_of_leaving)}` : ''}</Badge>}
              {s.guardian_app_installed
                ? <Badge tone="success">Parent app active</Badge>
                : s.guardian_name && <Badge tone="neutral">Parent app not set up</Badge>}
            </div>
            <p className="text-sm text-ink-soft mt-1">
              Class <b className="text-ink">{s.class_name || '—'}</b>
              {s.roll_number && <> · Roll <b className="text-ink">{s.roll_number}</b></>}
              {s.admission_no && <> · Adm. No. <b className="text-ink">{s.admission_no}</b></>}
              {s.class_teacher_name && <> · Class teacher {s.class_teacher_name}</>}
            </p>
            <dl className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-x-6 gap-y-2 mt-4 text-sm">
              <Field label="Father">{s.father_name}</Field>
              <Field label="Mother">{s.mother_name}</Field>
              <Field label="Guardian (app login)">{s.guardian_name}</Field>
              <Field label="Date of birth">{s.date_of_birth ? fmtDate(s.date_of_birth) : null}</Field>
              <Field label="Gender">{s.gender}</Field>
              <Field label="Blood group">{s.blood_group}</Field>
              <Field label="Category">{s.category}</Field>
              <Field label="Admitted on">{s.date_of_admission ? fmtDate(s.date_of_admission) : null}</Field>
            </dl>
          </div>
          <div className="flex flex-col gap-2 text-sm min-w-[200px]">
            {s.guardian_phone && <a href={`tel:${s.guardian_phone}`} className="flex items-center gap-2 text-transit-deep font-semibold hover:underline"><Phone size={14} /> {s.guardian_phone}</a>}
            {s.guardian_email && <a href={`mailto:${s.guardian_email}`} className="flex items-center gap-2 text-transit-deep font-semibold hover:underline break-all"><Mail size={14} /> {s.guardian_email}</a>}
            {(s.address || s.pickup_address) && <span className="flex items-start gap-2 text-ink-soft"><MapPin size={14} className="mt-0.5 shrink-0" /> {s.address || s.pickup_address}</span>}
            <div className="flex gap-2 mt-1">
              <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5 w-fit" onClick={() => onEdit(s.id)}>
                <Pencil size={13} /> Edit details
              </Button>
              {s.status === 'active' && (
                <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5 w-fit" onClick={openIdCard}>
                  <IdCardIcon size={13} /> ID card
                </Button>
              )}
              {onCertificate && (
                <Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5 w-fit" onClick={() => onCertificate(s.id)}>
                  <FileBadge size={13} /> Certificate
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* ── Fee strip ────────────────────────────────────── */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3 mb-2">
        <FeeBox label="Total charged" value={configured ? inr(total) : '—'} tone="text-ink" />
        <FeeBox label="Paid" value={configured ? inr(fees.amount_paid) : '—'} tone="text-success" />
        <FeeBox label="Remaining" value={configured ? inr(fees.amount_due) : '—'} tone={configured && fees.amount_due > 0 ? 'text-critical' : 'text-ink'}
          sub={configured && fees.pending_months > 0 ? `${fees.pending_months} month${fees.pending_months > 1 ? 's' : ''} since ${fmtMonth(fees.pending_since)}` : configured ? 'All clear' : 'Fees not set up'} />
        <div className="bg-paper-raised border border-line rounded-m p-4 flex flex-col justify-center gap-2">
          <Button onClick={notifyDues} disabled={!configured || sending} className="flex items-center justify-center gap-1.5">
            <Bell size={14} /> {sending ? 'Sending…' : 'Notify dues'}
          </Button>
          <button onClick={() => setTab('fees')} className="text-xs font-bold text-transit-deep hover:underline">Open fee ledger →</button>
        </div>
      </div>
      {notice && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s w-fit mb-2 ${notice.startsWith('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>{notice}</p>
      )}

      {/* ── Tabs ─────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-line mt-5 mb-4 overflow-x-auto" role="tablist">
        {TABS.map(t => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors
              ${tab === t.key ? 'border-transit text-transit-deep' : 'border-transparent text-ink-soft hover:text-ink'}`}>
            {t.label}
            {t.key === 'siblings' && profile.siblings.length > 0 && <span className="ml-1.5 text-xs">({profile.siblings.length})</span>}
          </button>
        ))}
      </div>

      {tab === 'attendance' && <AttendanceTab studentId={s.id} />}
      {tab === 'fees'       && <FeesTab student={s} fees={fees} onFeesChange={setFees} schoolName={schoolName} />}
      {tab === 'transport'  && <TransportTab profile={profile} />}
      {tab === 'results'    && <ResultsTab student={s} schoolName={schoolName} board={board} />}
      {tab === 'siblings'   && <SiblingsTab siblings={profile.siblings} onOpenStudent={onOpenStudent} />}
      {tab === 'payments'   && <PaymentsTab studentId={s.id} />}
      {tab === 'history'    && <HistoryTab studentId={s.id} />}
      {tab === 'documents'  && <DocumentsTab studentId={s.id} />}
      {tab === 'remarks'    && <RemarksTab studentId={s.id} />}
      {tab === 'leave'      && <LeaveTab studentId={s.id} />}

      {idCard && <IdCardSheet students={idCard.students} school={idCard.school} validTill={sessionEndISO()} onClose={() => setIdCard(null)} />}
    </div>
  );
}

function BackLink({ onBack }) {
  return (
    <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-bold text-transit-deep hover:underline mb-3">
      <ArrowLeft size={15} /> Back
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">{label}</dt>
      <dd className="text-ink font-semibold">{children || '—'}</dd>
    </div>
  );
}

function FeeBox({ label, value, tone, sub }) {
  return (
    <div className="bg-paper-raised border border-line rounded-m p-4">
      <span className="block text-[11px] font-bold uppercase tracking-wider text-ink-soft mb-1.5">{label}</span>
      <span className={`font-display font-extrabold text-2xl tabular-nums ${tone}`}>{value}</span>
      {sub && <span className="block text-xs text-ink-soft font-semibold mt-1">{sub}</span>}
    </div>
  );
}

// ── Attendance: month calendar of classroom roll-call ────────
const STATUS_STYLE = {
  present: 'bg-success-tint text-success',
  late:    'bg-caution-tint text-caution',
  absent:  'bg-critical-tint text-critical',
};

function AttendanceTab({ studentId }) {
  const [month, setMonth] = useState(() => todayISO().slice(0, 7));
  const [data,  setData]  = useState(null);
  const [msg,   setMsg]   = useState('');

  useEffect(() => {
    setData(null); setMsg('');
    getStudentAttendanceMonth(studentId, month)
      .then(setData)
      .catch(err => setMsg('Failed to load attendance: ' + (err.response?.data?.error || err.message)));
  }, [studentId, month]);

  function shift(delta) {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  const [y, m] = month.split('-').map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const firstDow = (new Date(y, m - 1, 1).getDay() + 6) % 7; // Monday-first
  const byDate = Object.fromEntries((data?.days || []).map(d => [d.date, d.status]));
  const attended = data ? data.counts.present + data.counts.late : 0;
  const rate = data?.total_marked ? Math.round((attended / data.total_marked) * 100) : null;

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4 items-start">
      <Card>
        <CardHeader title={fmtMonth(month)} action={
          <div className="flex gap-1">
            <button onClick={() => shift(-1)} aria-label="Previous month" className="p-1.5 rounded-s hover:bg-paper"><ChevronLeft size={16} /></button>
            <button onClick={() => shift(1)} aria-label="Next month" className="p-1.5 rounded-s hover:bg-paper"><ChevronRight size={16} /></button>
          </div>
        } />
        <div className="p-4">
          {msg && <p className="text-critical text-sm font-bold">{msg}</p>}
          <div className="grid grid-cols-7 gap-1.5 text-center">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <span key={i} className="text-[11px] font-bold text-ink-soft py-1">{d}</span>
            ))}
            {Array.from({ length: firstDow }).map((_, i) => <span key={`pad-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const date = `${month}-${String(i + 1).padStart(2, '0')}`;
              const status = byDate[date];
              return (
                <span key={date} title={status ? `${fmtDate(date)}: ${status}` : fmtDate(date)}
                  className={`h-9 grid place-items-center rounded-s text-[13px] font-semibold tabular-nums
                    ${status ? STATUS_STYLE[status] : 'text-ink-soft'}`}>
                  {i + 1}
                </span>
              );
            })}
          </div>
          <div className="flex gap-4 mt-4 text-xs font-semibold text-ink-soft">
            <Legend cls={STATUS_STYLE.present} label="Present" />
            <Legend cls={STATUS_STYLE.late} label="Late" />
            <Legend cls={STATUS_STYLE.absent} label="Absent" />
          </div>
        </div>
      </Card>
      <Card className="p-5">
        <span className="block text-[11px] font-bold uppercase tracking-wider text-ink-soft mb-1.5">Attendance rate</span>
        <span className="font-display font-extrabold text-4xl text-ink tabular-nums">{rate == null ? '—' : `${rate}%`}</span>
        {data && (
          <dl className="grid grid-cols-3 gap-3 mt-5 text-center">
            <Stat label="Present" value={data.counts.present} cls="text-success" />
            <Stat label="Late" value={data.counts.late} cls="text-caution" />
            <Stat label="Absent" value={data.counts.absent} cls="text-critical" />
          </dl>
        )}
        <p className="text-xs text-ink-soft mt-4">From the class teacher's daily roll-call in the staff app. Days with no mark are blank.</p>
      </Card>
    </div>
  );
}

function Legend({ cls, label }) {
  return <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-sm ${cls}`} />{label}</span>;
}

function Stat({ label, value, cls }) {
  return (
    <div className="bg-paper rounded-s py-2.5">
      <dd className={`font-display font-extrabold text-xl tabular-nums ${cls}`}>{value}</dd>
      <dt className="text-[11px] font-bold uppercase tracking-wider text-ink-soft">{label}</dt>
    </div>
  );
}

// ── Transport: route/van/driver + last 30 days of boarding ───
function TransportTab({ profile }) {
  const s = profile.student;
  if (!s.route_id) {
    return <p className="text-ink-soft text-sm">Not assigned to a bus route. Assign one from <b>Edit details</b>.</p>;
  }
  const absent = new Set(profile.bus_absences);
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4 items-start">
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-9 h-9 rounded-s bg-transit text-white grid place-items-center"><Bus size={18} /></span>
          <div>
            <div className="font-display font-bold text-ink">{s.route_name}</div>
            <div className="text-xs text-ink-soft">{s.van_number ? `Van ${s.van_number}` : 'No van assigned to this route'}</div>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field label="Pickup order">{s.stop_order ? `Stop ${s.stop_order}` : null}</Field>
          <Field label="Pickup point">{s.stop_name}</Field>
          <Field label="Driver">{s.driver_name}</Field>
          <Field label="Driver phone">{s.driver_phone && <a href={`tel:${s.driver_phone}`} className="text-transit-deep hover:underline">{s.driver_phone}</a>}</Field>
          <Field label="Home location">{s.home_latitude != null ? (s.pickup_address || 'Pinned on map') : 'Not pinned yet'}</Field>
          <Field label="Parent app">{s.guardian_app_installed ? <span className="flex items-center gap-1"><Smartphone size={13} /> Receiving bus alerts</span> : 'Not installed'}</Field>
        </dl>
      </Card>
      <Card>
        <CardHeader title="Last 30 days on the bus" />
        {!profile.trips.length && !profile.bus_absences.length && <p className="px-5 py-6 text-sm text-ink-soft">No trips recorded yet.</p>}
        {(profile.trips.length > 0 || profile.bus_absences.length > 0) && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2">Date</th><th className="px-4 py-2">Boarded</th><th className="px-4 py-2">Dropped</th>
              </tr>
            </thead>
            <tbody>
              {mergeTripDays(profile.trips, profile.bus_absences).map(row => (
                <tr key={row.date} className="border-t border-line-soft">
                  <td className="px-4 py-2.5 font-semibold">{fmtDate(row.date)}</td>
                  {absent.has(row.date) && !row.boarded_at
                    ? <td colSpan={2} className="px-4 py-2.5"><Badge tone="neutral">Parent marked not travelling</Badge></td>
                    : <>
                        <td className="px-4 py-2.5 tabular-nums">{fmtTime(row.boarded_at)}</td>
                        <td className="px-4 py-2.5 tabular-nums">{fmtTime(row.dropped_at)}</td>
                      </>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function mergeTripDays(trips, absences) {
  const rows = new Map(trips.map(t => [t.date, t]));
  for (const d of absences) if (!rows.has(d)) rows.set(d, { date: d });
  return [...rows.values()].sort((a, b) => b.date.localeCompare(a.date));
}

function SiblingsTab({ siblings, onOpenStudent }) {
  if (!siblings.length) return <p className="text-ink-soft text-sm">No siblings linked to the same guardian.</p>;
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
      {siblings.map(sib => (
        <button key={sib.id} onClick={() => onOpenStudent(sib.id)}
          className="text-left bg-paper-raised border border-line rounded-m p-4 flex items-center gap-3 hover:shadow-2 transition-shadow">
          <span className="w-10 h-10 rounded-full bg-clearance text-white grid place-items-center font-bold shrink-0">{initials(sib.name)}</span>
          <span>
            <span className="block font-semibold text-ink">{sib.name}</span>
            <span className="block text-xs text-ink-soft">Class {sib.class_name || '—'}{sib.roll_number ? ` · Roll ${sib.roll_number}` : ''}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

// ── History: class per academic year + certificates issued ──
const RESULT_LABEL = { promoted: 'Promoted', detained: 'Held back', passed_out: 'Passed out', left: 'Left' };

function HistoryTab({ studentId }) {
  const [rows,  setRows]  = useState(null);
  const [certs, setCerts] = useState([]);
  useEffect(() => {
    getClassHistory(studentId).then(setRows).catch(() => setRows([]));
    getCertificates({ student_id: studentId }).then(setCerts).catch(() => setCerts([]));
  }, [studentId]);
  if (!rows) return <p className="text-ink-soft text-sm">Loading…</p>;
  const label = { tc: 'Transfer Certificate', bonafide: 'Bonafide Certificate', character: 'Character Certificate' };
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4 items-start">
      <Card>
        <CardHeader title="Class history" />
        {rows.map(h => (
          <div key={h.id} className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line-soft first:border-t-0 text-sm">
            <span><b className="text-ink">{h.session_name || '—'}</b> · Class {h.class_name}{h.roll_number ? ` · Roll ${h.roll_number}` : ''}</span>
            <Badge tone={h.result === 'promoted' || h.result === 'passed_out' ? 'success' : 'caution'}>
              {RESULT_LABEL[h.result]}{h.result === 'promoted' && h.next_class_name ? ` → ${h.next_class_name}` : ''}
            </Badge>
          </div>
        ))}
        {!rows.length && <p className="px-5 py-6 text-sm text-ink-soft">No year-end promotions recorded yet.</p>}
      </Card>
      <Card>
        <CardHeader title="Certificates issued" />
        {certs.map(c => (
          <div key={c.id} className="flex justify-between px-5 py-3 border-t border-line-soft first:border-t-0 text-sm">
            <span>{label[c.cert_type]} <b>No. {c.serial_no}</b></span><span className="text-ink-soft">{fmtDate(c.issued_on)}</span>
          </div>
        ))}
        {!certs.length && <p className="px-5 py-6 text-sm text-ink-soft">None issued.</p>}
      </Card>
    </div>
  );
}
